import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { X, TrendingUp, TrendingDown } from 'lucide-react';
import { Modal, Button, MediaUpload } from '@components/ui';
import { mockCariAccounts, MOVEMENT_SUBTYPE_LABELS, PURCHASE_SUBTYPES, PAYMENT_SUBTYPES } from '@mocks/cariHesaplar';
import { toast } from 'react-toastify';
import clsx from 'clsx';

export default function HareketModal({ isOpen, onClose, onSubmit, defaultAccountId = null, defaultType = 'purchase' }) {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const { constructions } = useSelector((state) => state.construction);

    const [type, setType] = useState(defaultType); // 'purchase' | 'payment'
    const [form, setForm] = useState({
        accountId: defaultAccountId || '',
        constructionId: showAllConstructions ? '' : (selectedConstruction?.id || ''),
        date: new Date().toISOString().split('T')[0],
        subtype: type === 'purchase' ? 'fatura' : 'nakit',
        amount: '',
        description: '',
        document_url: null,
    });

    // Firma değişince tip varsa reset sub
    useEffect(() => {
        setForm(prev => ({
            ...prev,
            subtype: type === 'purchase' ? 'fatura' : 'nakit',
        }));
    }, [type]);

    // Modal açılınca defaultları uygula
    useEffect(() => {
        if (isOpen) {
            setType(defaultType);
            setForm({
                accountId: defaultAccountId || '',
                constructionId: showAllConstructions ? '' : (selectedConstruction?.id || ''),
                date: new Date().toISOString().split('T')[0],
                subtype: defaultType === 'purchase' ? 'fatura' : 'nakit',
                amount: '',
                description: '',
                document_url: null,
            });
        }
    }, [isOpen, defaultAccountId, defaultType, selectedConstruction, showAllConstructions]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.accountId) { toast.error('Firma seçiniz'); return; }
        if (!form.amount || Number(form.amount) <= 0) { toast.error('Geçerli bir tutar giriniz'); return; }
        if (!form.description.trim()) { toast.error('Açıklama giriniz'); return; }

        const account = mockCariAccounts.find(a => a.id === form.accountId);
        onSubmit({
            ...form,
            type,
            amount: Number(form.amount),
            companyName: account?.companyName || '',
        });

        const msg = type === 'purchase' ? 'Alış/Fatura kaydedildi' : 'Ödeme kaydedildi';
        toast.success(msg);
        onClose();
    };

    const isPurchase = type === 'purchase';

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Yeni Hareket Ekle" size="md">
            {/* Type Toggle */}
            <div className="flex gap-2 mb-6">
                <button
                    type="button"
                    onClick={() => setType('purchase')}
                    className={clsx(
                        'flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition-all',
                        isPurchase
                            ? 'border-error bg-error/10 text-error'
                            : 'border-border-light text-text-secondary hover:border-error/40'
                    )}
                >
                    <TrendingUp size={18} />
                    Alış / Fatura
                    <span className="text-xs font-normal">(Borç ekler)</span>
                </button>
                <button
                    type="button"
                    onClick={() => setType('payment')}
                    className={clsx(
                        'flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 text-sm font-semibold transition-all',
                        !isPurchase
                            ? 'border-success bg-success/10 text-success'
                            : 'border-border-light text-text-secondary hover:border-success/40'
                    )}
                >
                    <TrendingDown size={18} />
                    Ödeme
                    <span className="text-xs font-normal">(Borç azaltır)</span>
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Firma */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Firma *</label>
                    <select
                        className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                        value={form.accountId}
                        onChange={(e) => handleChange('accountId', e.target.value)}
                        disabled={!!defaultAccountId}
                        required
                    >
                        <option value="">Firma seçiniz...</option>
                        {mockCariAccounts.map(a => (
                            <option key={a.id} value={a.id}>{a.companyName} ({a.companyTypeLabel})</option>
                        ))}
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    {/* İnşaat */}
                    {showAllConstructions && (
                        <div className="col-span-2 sm:col-span-1">
                            <label className="block text-sm font-medium text-text-secondary mb-1.5">İnşaat</label>
                            <select
                                className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                                value={form.constructionId}
                                onChange={(e) => handleChange('constructionId', e.target.value)}
                            >
                                <option value="">Belirtme</option>
                                {(constructions || []).map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Tarih */}
                    <div className={clsx(showAllConstructions ? 'col-span-2 sm:col-span-1' : 'col-span-2 sm:col-span-1')}>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">Tarih *</label>
                        <input
                            type="date"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                            value={form.date}
                            onChange={(e) => handleChange('date', e.target.value)}
                            required
                        />
                    </div>

                    {/* Tür / Alt tip */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">
                            {isPurchase ? 'Fatura Türü' : 'Ödeme Yöntemi'}
                        </label>
                        <select
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                            value={form.subtype}
                            onChange={(e) => handleChange('subtype', e.target.value)}
                        >
                            {(isPurchase ? PURCHASE_SUBTYPES : PAYMENT_SUBTYPES).map(s => (
                                <option key={s} value={s}>{MOVEMENT_SUBTYPE_LABELS[s]}</option>
                            ))}
                        </select>
                    </div>

                    {/* Tutar */}
                    <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">Tutar (₺) *</label>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                            value={form.amount}
                            onChange={(e) => handleChange('amount', e.target.value)}
                            required
                        />
                    </div>
                </div>

                {/* Açıklama */}
                <div>
                    <label className="block text-sm font-medium text-text-secondary mb-1.5">Açıklama *</label>
                    <input
                        type="text"
                        placeholder={isPurchase ? 'ör: Beton 3. parti faturası' : 'ör: Ağustos ödemesi'}
                        className="w-full px-4 py-3 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary"
                        value={form.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        required
                    />
                </div>

                {/* Belge Yükleme */}
                <MediaUpload
                    mode="single"
                    accept="all"
                    label={isPurchase ? 'Fatura / İrsaliye' : 'Ödeme Belgesi / Dekont'}
                    hint="Görsel veya PDF"
                    maxSizeMB={10}
                    compact
                    onFilesChange={(files) => handleChange('document_url', files.length > 0 ? files[0] : null)}
                />

                {/* Footer */}
                <div className="flex gap-3 pt-2">
                    <Button type="button" variant="ghost" onClick={onClose} className="flex-1">
                        İptal
                    </Button>
                    <Button
                        type="submit"
                        variant={isPurchase ? 'error' : 'success'}
                        className="flex-1"
                    >
                        {isPurchase ? 'Alış Ekle' : 'Ödeme Ekle'}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
